import React, { useState, useEffect, memo } from 'react';
import { X } from 'lucide-react';
import api from '../services/api';
const AvailableColumns = memo(({ 
  datasetId, 
  onFeatureSelect, 
  onTargetSelect,
  selectedFeatures,
  targetColumn
}) => {
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchColumns = async () => {
      if (!datasetId) return;

      try {
        setLoading(true);
        const response = await api.get(`/datasets/${datasetId}`);
        const csvLines = response.data.split('\n');
        const columns = csvLines[0].split(',').map(col => col.trim());
        
        if (isMounted) {
          setColumns(columns);
          setError(null);
        }
      } catch (error) {
        console.error('Error fetching columns:', error);
        if (isMounted) {
          setError('Failed to load columns');
          setColumns([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchColumns();

    return () => {
      isMounted = false;
    };
  }, [datasetId]);

  const handleColumnClick = (column) => {
    if (column === targetColumn) {
      return; // Column is already selected as target
    }
    if (!selectedFeatures.includes(column)) {
      onFeatureSelect([...selectedFeatures, column]);
    }
  };

  const handleTargetClick = (column) => {
    if (selectedFeatures.includes(column)) {
      onFeatureSelect(selectedFeatures.filter(f => f !== column));
    }
    onTargetSelect(column);
  };

  const removeFeature = (column) => {
    onFeatureSelect(selectedFeatures.filter(f => f !== column));
  };

  if (loading) {
    return (
      <div className="text-sm text-gray-500 animate-pulse">
        Loading available columns...
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-sm text-red-600">
        {error}
      </div>
    );
  }

  if (columns.length === 0) {
    return (
      <div className="text-sm text-amber-600">
        No columns found. Please check the dataset.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <label className="block font-semibold mb-2">Available Columns</label>
        <div className="flex flex-wrap gap-2 bg-gray-50 p-3 rounded-lg">
          {columns.map((col, index) => {
            const isFeature = selectedFeatures.includes(col);
            const isTarget = col === targetColumn;
            
            return (
              <div
                key={index}
                className={`
                  px-3 py-1 rounded-full text-xs cursor-pointer transition-colors
                  ${isFeature ? 'bg-blue-100 text-blue-800' : 
                    isTarget ? 'bg-green-100 text-green-800' :
                    'bg-gray-100 text-gray-800 hover:bg-gray-200'}
                `}
                onClick={() => handleColumnClick(col)}
                onContextMenu={(e) => {
                  e.preventDefault();
                  handleTargetClick(col);
                }}
                title={`Left click to add as feature, right click to set as target`}
              >
                {col}
              </div>
            );
          })}
        </div>
        <p className="text-xs text-gray-500 mt-1">
          Left click to select as feature, right click to set as target
        </p>
      </div>

      <div>
        <label className="block font-semibold mb-2">Selected Features</label>
        <div className="flex flex-wrap gap-2 bg-blue-50 p-3 rounded-lg min-h-12">
          {selectedFeatures.map((feature, index) => (
            <div
              key={index}
              className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs flex items-center gap-1"
            >
              {feature}
              <button
                onClick={() => removeFeature(feature)}
                className="hover:text-blue-600"
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div>
        <label className="block font-semibold mb-2">Target Column</label>
        <div className="flex flex-wrap gap-2 bg-green-50 p-3 rounded-lg min-h-12">
          {targetColumn && (
            <div className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs flex items-center gap-1">
              {targetColumn}
              <button
                onClick={() => onTargetSelect('')}
                className="hover:text-green-600"
              >
                <X size={12} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

AvailableColumns.displayName = 'AvailableColumns';

export default AvailableColumns;